import { Inject, inject, Injectable, Injector } from '@angular/core';
import { isDefined } from '@sama/js-core';
import { isEmpty } from '@sama/micro-dash';
import { withHistory } from '@sama/rxjs-core';
import { isEqual } from 'lodash';
import { fromEvent, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import { DebouncedEventTracker } from '../../../cross-team-shared/event-tracking/debounced-event-tracker';
import { buildTrackerFromShellConnector } from '../../../cross-team-shared/event-tracking/event-utils';
import {
  MouseButton,
  MouseEventService,
} from '../../../cross-team-shared/misc-services/events/mouse-event.service';
import { ViewBox } from '../../../team-apis/content/view-box';
import {
  ShellConnector,
  shellConnectorToken,
} from '../../../team-apis/core/shell-connector';
import { Point2D } from '../../../team-apis/otterdam/answers/shape-typings';
import { MouseCoordinateService } from '../mouse-coordinate.service';
import { AbstractSensorContentStore2D } from '../state/abstract-sensor-content-store-2d';
import { PanningDragHandler } from '../tools/shared/drag-handlers/panning-drag-handler';
import { AbstractKeyboardMovementService } from './abstract-keyboard-movement-service';
import { ViewBoxService } from './view-box.service';

@Injectable()
export class PanningService extends AbstractKeyboardMovementService {
  private panningDragHandler?: PanningDragHandler;
  private mouseCoordinateService = inject(MouseCoordinateService);
  private viewBoxService = inject(ViewBoxService);

  constructor(
    private mouseEventService: MouseEventService,
    @Inject(shellConnectorToken)
    private shellConnector: ShellConnector,
    private store: AbstractSensorContentStore2D,
    private injector: Injector,
  ) {
    super(injector);
  }

  startListening(requiredEnabledElement?: HTMLElement) {
    this.unsubscribe(); // this method is called for each new task
    this.startMouseListening();
    this.setUpKeyboardShortcutPanning(requiredEnabledElement);
    this.setUpEventListener();
  }

  private startMouseListening() {
    this.subscribeTo(
      this.mouseEventService
        .getEvent$('container', 'mousedown', { button: MouseButton.Right })
        .pipe(map((eventInfo) => eventInfo.event)),
      this.startPanning,
    );

    this.subscribeTo(
      fromEvent<MouseEvent>(document, 'mousemove'),
      this.onMouseMove,
    );
  }

  private setUpKeyboardShortcutPanning(requiredEnabledElement?: HTMLElement) {
    // Adjust panning used in 3D for smaller pixel precisions.
    const getPanningDelta = () =>
      this.store.state().pixelSize > 0.01 ? 20 : 1;
    const offset$ = this.createOffset$(
      {
        leftShortcutName: 'panLeft',
        rightShortcutName: 'panRight',
        downShortcutName: 'panDown',
        upShortcutName: 'panUp',
      },
      getPanningDelta,
    ).pipe(
      filter(
        () =>
          !requiredEnabledElement ||
          !!requiredEnabledElement.closest('[movement-disabled=false]'),
      ),
    );
    this.subscribeToOffset(offset$);
  }

  private subscribeToOffset(offset$: Observable<Point2D>) {
    this.subscribeTo(offset$, ([offsetX, offsetY]) => {
      if (
        this.store.state().editMode === 'none' ||
        isEmpty(this.store.state().selection)
      ) {
        this.viewBoxService.panWithOffset(
          [offsetX, offsetY],
          'movement_keyboard_shortcut',
        );
        void this.mouseCoordinateService.update();
      }
    });
  }

  private startPanning(event: MouseEvent) {
    this.panningDragHandler = new PanningDragHandler(event, this.injector);
    this.panningDragHandler.startListening();

    void this.panningDragHandler.completion.finally(() => {
      this.panningDragHandler = undefined;
    });
  }

  private onMouseMove(event: MouseEvent) {
    // Normally this is not needed, but for some reason when a mouseup with the
    // right button happens outside the document (e.g. over the address bar, or
    // outside the browser window) the event is not sent. So we additionally
    // monitor mousemoves to detect when the user no longer has the right
    // button pressed.
    if (this.panningDragHandler && event.buttons !== 2) {
      void this.panningDragHandler.stopListening();
    }
  }

  private setUpEventListener() {
    const eventTracker = new DebouncedEventTracker(
      { id: 'pan_canvas', bucket: 'navigation', sub_bucket: 'frame' },
      buildTrackerFromShellConnector(this.shellConnector),
    );
    this.subscribeTo(
      this.store('viewBox').$.pipe(
        withHistory(1),
        filter(this.panHasChanged.bind(this)),
        distinctUntilChanged(isEqual),
      ),
      ([currentViewBox, previousViewBox]) => {
        const isFirstTime = !eventTracker.isDebouncePending();

        if (isFirstTime) {
          eventTracker.startEvent();
          eventTracker.addContext({
            start_x: previousViewBox[0],
            start_y: previousViewBox[1],
          });
        }

        eventTracker.trackWithDebounce({
          end_x: currentViewBox[0],
          end_y: currentViewBox[1],
          last_source_used: this.viewBoxService.getLastPanSourceUsed(),
          batch_included_wheel: false,
        });
      },
    );
  }

  private panHasChanged([newViewBox, oldViewBox]: [
    newViewBox: ViewBox,
    oldViewBox?: ViewBox,
  ]) {
    return (
      isDefined(this.viewBoxService.getLastPanSourceUsed()) &&
      isDefined(oldViewBox) &&
      newViewBox[2] === oldViewBox[2] &&
      newViewBox[3] === oldViewBox[3]
    );
  }
}
